import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDocumentMeta } from '../../hooks/useDocumentMeta';
import masterData from '../../data/master_data.json';
import { allBlogArticles } from '../../mocks/blogArticles';
import { generateSitemapUrls } from '../../utils/sitemapGenerator';

interface GroupedUrls {
  label: string;
  icon: string;
  urls: { loc: string; lastmod: string; changefreq: string; priority: string }[];
}

const SitemapPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'visual' | 'xml'>('visual');
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({
    core: true,
    calculators: true,
    blog: true,
  });
  const [copiedXml, setCopiedXml] = useState(false);

  const urls = useMemo(
    () => generateSitemapUrls(masterData as unknown as Record<string, unknown>, allBlogArticles),
    [],
  );

  const grouped: GroupedUrls[] = useMemo(() => {
    const core = urls.filter(
      (u) => u.loc.endsWith('/') || u.loc.endsWith('/blog') || u.loc.endsWith('/sitemap'),
    );
    const calculators = urls.filter(
      (u) => !u.loc.includes('/blog') && !core.includes(u),
    );
    const blog = urls.filter((u) => u.loc.includes('/blog/'));

    return [
      { label: 'Core Pages', icon: 'ri-home-4-line', urls: core },
      { label: `Calculator Pages (${calculators.length})`, icon: 'ri-calculator-line', urls: calculators },
      { label: `Blog Articles (${blog.length})`, icon: 'ri-article-line', urls: blog },
    ];
  }, [urls]);

  const xmlContent = useMemo(() => {
    const entries = urls
      .map(
        (u) =>
          `  <url>\n    <loc>${u.loc}</loc>\n    <lastmod>${u.lastmod}</lastmod>\n    <changefreq>${u.changefreq}</changefreq>\n    <priority>${u.priority}</priority>\n  </url>`,
      )
      .join('\n');
    return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${entries}\n</urlset>`;
  }, [urls]);

  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleCopyXml = () => {
    navigator.clipboard.writeText(xmlContent);
    setCopiedXml(true);
    setTimeout(() => setCopiedXml(false), 2000);
  };

  const getPriorityColor = (p: string) => {
    const v = parseFloat(p);
    if (v >= 0.9) return 'bg-emerald-100 text-emerald-700';
    if (v >= 0.7) return 'bg-teal-100 text-teal-700';
    if (v >= 0.5) return 'bg-amber-100 text-amber-700';
    return 'bg-gray-100 text-gray-600';
  };

  const getFreqBadge = (f: string) => {
    if (f === 'daily') return 'bg-rose-50 text-rose-600';
    if (f === 'weekly') return 'bg-sky-50 text-sky-600';
    return 'bg-gray-50 text-gray-500';
  };

  useDocumentMeta({
    title: 'Sitemap - Salary Tax Calculator | All Pages Index',
    description:
      'Complete sitemap of Salary Tax Calculator. Browse all calculator pages, blog articles, and resources indexed for search engines.',
    keywords: 'sitemap, salary calculator pages, tax calculator index, blog articles index',
    canonical: 'https://mysalarycalculator.net/sitemap',
    ogTitle: 'Sitemap - Salary Tax Calculator | All Pages Index',
    ogDescription: 'Browse all pages on Salary Tax Calculator — calculator pages for every city, state, and country plus 36 blog articles on tax strategy, freelancer taxes, retirement planning, and remote work.',
    ogUrl: 'https://mysalarycalculator.net/sitemap',
    ogImage: 'https://public.readdy.ai/ai/img_res/520c7c33eaa3a528d2381b7757ba9376.jpg',
    twitterCard: 'summary_large_image',
    twitterTitle: 'Sitemap - Salary Tax Calculator | All Pages Index',
    twitterDescription: 'Browse all pages on Salary Tax Calculator — calculator pages for every city, state, and country plus 36 blog articles on tax strategy, freelancer taxes, retirement planning, and remote work.',
    twitterImage: 'https://readdy.ai/api/search-image?query=A%20clean%20minimalist%20sitemap%20visualization%20with%20interconnected%20nodes%20and%20lines%20representing%20website%20structure%20on%20a%20light%20teal%20and%20white%20background%20with%20subtle%20grid%20pattern%20professional%20web%20design%20concept&width=1200&height=630&seq=sitemap-og-share&orientation=landscape',
    metaTags: [
      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: 'Salary Tax Calculator' },
      { name: 'author', content: 'Salary Tax Calculator' },
      { name: 'robots', content: 'index, follow' },
    ],
    structuredData: [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://mysalarycalculator.net/" },
          { "@type": "ListItem", "position": 2, "name": "Sitemap", "item": "https://mysalarycalculator.net/sitemap" }
        ]
      },
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Sitemap - Salary Tax Calculator",
        "description": "Complete sitemap of Salary Tax Calculator. Browse all calculator pages, blog articles, and resources indexed for search engines.",
        "url": "https://mysalarycalculator.net/sitemap",
        "isPartOf": {
          "@type": "WebSite",
          "name": "Salary Tax Calculator",
          "url": "https://mysalarycalculator.net/"
        }
      }
    ]
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm" role="banner">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" aria-label="Main navigation">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <a href="/" aria-label="Salary Tax Calculator Home">
                <img
                  src="https://public.readdy.ai/ai/img_res/8f3cc483-3168-4437-a291-d29def05c8fe.png"
                  alt="Salary Tax Calculator Logo"
                  className="h-10 w-auto cursor-pointer"
                  width="40"
                  height="40"
                />
              </a>
              <div className="hidden md:block">
                <span className="text-xl font-bold text-gray-900">
                  <a href="/" className="hover:text-teal-600 transition-colors cursor-pointer">
                    Salary Tax Calculator
                  </a>
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-6">
              <a
                href="/"
                className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors cursor-pointer hidden md:block"
              >
                Calculator
              </a>
              <a
                href="/blog"
                className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors cursor-pointer hidden md:block"
              >
                Blog
              </a>
              <a
                href="/states"
                className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors cursor-pointer hidden md:block"
              >
                US States
              </a>
            </div>
          </div>
        </nav>
      </header>

      <main role="main" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Page Header */}
        <div className="mb-10">
          <nav className="flex items-center space-x-2 text-sm mb-6" aria-label="Breadcrumb">
            <a href="/" className="text-gray-500 hover:text-gray-700 transition-colors cursor-pointer flex items-center gap-1">
              <i className="ri-home-4-line text-xs"></i>
              Home
            </a>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-gray-900 font-medium">Sitemap</span>
          </nav>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            <strong>Salary Tax Calculator Sitemap</strong>
          </h1>
          <p className="text-gray-600 text-base max-w-2xl">
            A complete index of all <strong>{urls.length} pages</strong> on Salary Tax Calculator,
            including city salary calculators, state tax guides, and blog articles.
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          {grouped.map((g) => (
            <div
              key={g.label}
              className="bg-gray-50 rounded-lg p-4 border border-gray-100"
            >
              <div className="flex items-center gap-2 mb-1">
                <div className="w-7 h-7 flex items-center justify-center bg-white rounded-md border border-gray-200">
                  <i className={`${g.icon} text-sm text-gray-600`}></i>
                </div>
                <span className="text-2xl font-bold text-gray-900">{g.urls.length}</span>
              </div>
              <p className="text-xs text-gray-500">{g.label}</p>
            </div>
          ))}
        </div>

        {/* Tab Switcher */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-full p-1 w-fit mb-8">
          <button
            onClick={() => setActiveTab('visual')}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
              activeTab === 'visual'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <i className="ri-layout-grid-line mr-1.5"></i>Visual Map
          </button>
          <button
            onClick={() => setActiveTab('xml')}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
              activeTab === 'xml'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <i className="ri-code-s-slash-line mr-1.5"></i>XML Output
          </button>
        </div>

        {/* Visual Tab */}
        {activeTab === 'visual' && (
          <div className="space-y-4">
            {grouped.map((group, gi) => {
              const key = ['core', 'calculators', 'blog'][gi];
              const isExpanded = expandedGroups[key];
              return (
                <div key={key} className="border border-gray-200 rounded-xl overflow-hidden">
                  <button
                    onClick={() => toggleGroup(key)}
                    className="w-full flex items-center justify-between px-5 py-4 bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center bg-white rounded-lg border border-gray-200">
                        <i className={`${group.icon} text-gray-700`}></i>
                      </div>
                      <span className="font-semibold text-gray-900 text-sm">{group.label}</span>
                    </div>
                    <i
                      className={`ri-arrow-down-s-line text-gray-400 transition-transform ${
                        isExpanded ? 'rotate-180' : ''
                      }`}
                    ></i>
                  </button>
                  {isExpanded && (
                    <div className="divide-y divide-gray-100">
                      {group.urls.map((url) => {
                        const path = url.loc.replace('https://mysalarycalculator.net', '') || '/';
                        return (
                          <a
                            key={url.loc}
                            href={path}
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(path);
                            }}
                            className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors cursor-pointer group"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <i className="ri-link text-gray-300 group-hover:text-teal-500 transition-colors text-sm"></i>
                              <span className="text-sm text-gray-700 group-hover:text-teal-600 transition-colors truncate">
                                {path}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                              <span
                                className={`text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${getFreqBadge(
                                  url.changefreq,
                                )}`}
                              >
                                {url.changefreq}
                              </span>
                              <span
                                className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${getPriorityColor(
                                  url.priority,
                                )}`}
                              >
                                {url.priority}
                              </span>
                            </div>
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* XML Tab */}
        {activeTab === 'xml' && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-gray-500">
                Copy this XML and place it at <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">/sitemap.xml</code>
              </p>
              <button
                onClick={handleCopyXml}
                className="flex items-center gap-1.5 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className={copiedXml ? 'ri-check-line' : 'ri-file-copy-line'}></i>
                {copiedXml ? 'Copied!' : 'Copy XML'}
              </button>
            </div>
            <div className="bg-gray-950 rounded-xl p-5 overflow-auto max-h-[600px]">
              <pre className="text-xs text-gray-300 font-mono leading-relaxed whitespace-pre">
                {xmlContent}
              </pre>
            </div>
          </div>
        )}

        {/* Info Section */}
        <div className="mt-12 bg-teal-50 border border-teal-100 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-teal-500 rounded-lg flex-shrink-0">
              <i className="ri-information-line text-white text-sm"></i>
            </div>
            <div>
              <h3 className="font-semibold text-teal-900 mb-1">Auto-Generated Sitemap</h3>
              <p className="text-sm text-teal-700 leading-relaxed">
                This sitemap is automatically generated from the location database and blog articles.
                When new cities or articles are added, they are instantly included. The XML version at{' '}
                <a href="/sitemap.xml" className="underline font-medium cursor-pointer" rel="nofollow">
                  /sitemap.xml
                </a>{' '}
                is submitted to search engines for indexing.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 border-t border-gray-800" role="contentinfo">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="grid grid-cols-1 md:grid-cols-4 gap-8" aria-label="Footer navigation">
            <div>
              <h3 className="text-lg font-bold mb-4">Salary Calculator</h3>
              <p className="text-gray-400 text-sm">
                Accurate tax calculations for locations worldwide.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">
                <a href="/blog" className="hover:text-teal-400 transition-colors cursor-pointer">
                  Popular Articles
                </a>
              </h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>
                  <a href="/blog/manhattan-tax-tips-2026" className="hover:text-white transition-colors cursor-pointer">
                    Manhattan Tax Tips
                  </a>
                </li>
                <li>
                  <a href="/blog/san-francisco-tech-taxes" className="hover:text-white transition-colors cursor-pointer">
                    SF Tech Tax Guide
                  </a>
                </li>
                <li>
                  <a href="/blog/austin-no-state-tax-guide" className="hover:text-white transition-colors cursor-pointer">
                    Austin Zero Tax Guide
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Calculators</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>
                  <a href="/usa/ny/manhattan" className="hover:text-white transition-colors cursor-pointer">
                    Manhattan, NY
                  </a>
                </li>
                <li>
                  <a href="/usa/ca/san-francisco" className="hover:text-white transition-colors cursor-pointer">
                    San Francisco, CA
                  </a>
                </li>
                <li>
                  <a href="/uk/london" className="hover:text-white transition-colors cursor-pointer">
                    London, UK
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>
                  <a href="/privacy" className="hover:text-white transition-colors cursor-pointer">
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="/terms" className="hover:text-white transition-colors cursor-pointer">
                    Terms of Service
                  </a>
                </li>
              </ul>
            </div>
          </nav>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm text-gray-400">
            <p>
              &copy; 2026 Salary Tax Calculator. All rights reserved. |{' '}
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white transition-colors"
              >
                Powered by Readdy
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SitemapPage;
